import React, { useMemo, useEffect, useRef } from 'react';
import { useWatch } from 'react-hook-form';
import type { FormConditionalNode } from '../../types/layout';
import { evaluateCondition } from '../../evaluators/conditionEvaluator';
import type { ConditionContext } from '../../evaluators/conditionEvaluator';
import { useFormEngineForm, useFormEngineState } from '../../context/FormEngineContext';
import { FormNodeRenderer } from '../FormNodeRenderer';
import { prefixCondition } from '../../processors/pathRewriter';
import { collectFieldPathsFromLayout } from '../../utils/collectLayoutFieldPaths';

interface FormConditionalRendererProps {
  node: FormConditionalNode;
  namePrefix?: string;
}

export const FormConditionalRenderer = React.memo(function FormConditionalRenderer({
  node,
  namePrefix,
}: FormConditionalRendererProps): React.ReactElement | null {
  const { form } = useFormEngineForm();
  const { meta, getOptionProp, isReadOnly } = useFormEngineState();

  // Prefix watch fields for repeatable mode (row-scoped paths)
  const watchFields = useMemo(() => {
    const raw = Array.isArray(node.watch) ? node.watch : [node.watch];
    return namePrefix ? raw.map((f) => `${namePrefix}.${f}`) : raw;
  }, [node.watch, namePrefix]);

  const watchedValues = useWatch({ control: form.control, name: watchFields as any[] });

  // Prefix condition field references for repeatable mode
  const resolvedCondition = useMemo(() => {
    return namePrefix ? prefixCondition(node.condition, namePrefix) : node.condition;
  }, [node.condition, namePrefix]);

  const isVisible = useMemo(() => {
    const values = form.getValues();
    const fieldValue = Array.isArray(watchedValues) ? watchedValues[0] : watchedValues;
    const ctx: ConditionContext = {
      fieldValue,
      values,
      meta,
      getOptionProp,
    };
    return evaluateCondition(resolvedCondition, ctx);
  }, [watchedValues, resolvedCondition, meta, getOptionProp, form]);

  const prevVisibleRef = useRef<boolean | undefined>(undefined);

  useEffect(() => {
    const prev = prevVisibleRef.current;
    if (prev === undefined) {
      prevVisibleRef.current = isVisible;
      return;
    }

    if (prev !== isVisible && !isReadOnly) {
      const hiddenBranch = isVisible ? node.else : node.children;
      if (hiddenBranch?.length) {
        const fieldPaths = collectFieldPathsFromLayout(hiddenBranch, namePrefix);
        for (const fieldPath of fieldPaths) {
          form.resetField(fieldPath as never, { defaultValue: undefined });
        }
      }
    }

    prevVisibleRef.current = isVisible;
  }, [isVisible, node.children, node.else, namePrefix, form]);

  const children = isVisible ? node.children : node.else;
  if (!children || children.length === 0) return null;

  return (
    <>
      {children.map((child, idx) => (
        <FormNodeRenderer key={idx} node={child} namePrefix={namePrefix} />
      ))}
    </>
  );
});
